import { useDealTypeCategoryQuery } from "@api/dealTypeCategory/dealTypeCategory";
import { DEALS_PARAGRAPH_CODE } from "@EditArticleContainer/shared/constants/editArticle.constants";
import { DealsParagraphContents } from "@EditArticleContainer/shared/types/paragraphContents/paragraphContents.types";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import { useCreateDealTypeAndStatusContent } from "./hooks/useCreateAdditionalInformationContent";
import { useCreateDealLASCContent } from "./hooks/useCreateDealLASCContent";
import { useCreateDealMAContent } from "./hooks/useCreateDealMAContent";
import { useCreateDealTermsContent } from "./hooks/useCreateDealTermsContent";
import { useCreateAdditionalInformationContent } from "./hooks/useCreateDealTypeAndStatusContent";
import { useCreateDrugProfileContent } from "./hooks/useCreateDrugProfileContent";
import { useCreateKeyCommentsContent } from "./hooks/useCreateKeyCommentsContent";
import { useCreateTitleContent } from "./hooks/useCreateTitleContent";
import { useCreateTopicContent } from "./hooks/useCreateTopicContent";

/** undefined 빌더 결과는 payload에서 제외한다. */
const withDefined = <T extends Record<string, unknown>>(
  entries: T,
): Partial<T> =>
  Object.fromEntries(
    Object.entries(entries).filter(([, value]) => value !== undefined),
  ) as Partial<T>;

export const useCreateDealsContent = () => {
  const createTitle = useCreateTitleContent();
  const createTopic = useCreateTopicContent();
  const createDrugProfile = useCreateDrugProfileContent();
  const createKeyComments = useCreateKeyCommentsContent();
  const createDealTypeAndStatus = useCreateDealTypeAndStatusContent();
  const createAdditionalInformation = useCreateAdditionalInformationContent();
  const createDealTerms = useCreateDealTermsContent();

  const { data: dealTypeCategory } = useDealTypeCategoryQuery();
  const { buildPartnerAxisLASC, buildKeyRightsLASC, buildFinancialTermsLASC } =
    useCreateDealLASCContent();
  const {
    buildPartnerAxisMA,
    buildKeyRightsMA,
    buildFinancialTermsMA,
    buildMAStructure,
    buildKeyCommentsList,
  } = useCreateDealMAContent();

  const createCategoryContent = async (): Promise<
    Partial<DealsParagraphContents>
  > => {
    if (dealTypeCategory === "LA") {
      const [partnerAxis, keyRights, financialTerms] = await Promise.all([
        buildPartnerAxisLASC(),
        buildKeyRightsLASC(),
        buildFinancialTermsLASC(),
      ]);
      return withDefined({
        [DEALS_PARAGRAPH_CODE.partnerAxisLASC]: partnerAxis,
        [DEALS_PARAGRAPH_CODE.keyRightsLASC]: keyRights,
        [DEALS_PARAGRAPH_CODE.financialTermsLASC]: financialTerms,
      });
    }

    if (dealTypeCategory === "MA") {
      const [partnerAxis, keyRights, financialTerms, maStructure, keyComments] =
        await Promise.all([
          buildPartnerAxisMA(),
          buildKeyRightsMA(),
          buildFinancialTermsMA(),
          buildMAStructure(),
          buildKeyCommentsList(),
        ]);
      return withDefined({
        [DEALS_PARAGRAPH_CODE.partnerAxisMA]: partnerAxis,
        [DEALS_PARAGRAPH_CODE.keyRightsMA]: keyRights,
        [DEALS_PARAGRAPH_CODE.financialTermsMA]: financialTerms,
        [DEALS_PARAGRAPH_CODE.maStructure]: maStructure,
        [DEALS_PARAGRAPH_CODE.keyCommentsList]: keyComments,
      });
    }

    return {};
  };

  return async (): Promise<DealsParagraphContents> => {
    const title = createTitle();
    const topic = createTopic();
    const drugProfile = createDrugProfile();
    const keyComments = createKeyComments();
    const dealTypeAndStatus = createDealTypeAndStatus();
    const dealTerms = await createDealTerms();
    const additionalInformation = createAdditionalInformation();
    const categoryContent = await createCategoryContent();

    return {
      "Article Id": Number(getPressIdFromURL()),
      TC101: title,
      TC102: topic,
      TC103: drugProfile,
      TC110: dealTypeAndStatus,
      TC111: dealTerms,
      TC112: additionalInformation,
      TC106: keyComments,
      ...categoryContent,
    };
  };
};
